import { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-use';
import * as iterableApi from '../api';

interface UseInAppMessageReturnValue {
  displayedMessages: iterableApi.InAppMessage[];
  consumeMessage: (message: iterableApi.InAppMessage, isSilent?: boolean) => Promise<void>;
  trackMessageClose: (message: iterableApi.InAppMessage) => Promise<void>;
  trackMessageClick: (message: iterableApi.InAppMessage, clickedUrl: string) => Promise<void>;
  trackMessageDelivery: (message: iterableApi.InAppMessage) => Promise<void>;
  trackMessageOpen: (message: iterableApi.InAppMessage) => Promise<void>;
}

export const useInAppMessage = (userId: string): UseInAppMessageReturnValue => {
  const [displayedMessages, setDisplayedMessages] = useState<iterableApi.InAppMessage[]>([]);
  const [consumedMessages, setConsumedMessages] = useState<iterableApi.InAppMessage[]>([]);
  const location = useLocation();

  const findMatchingMessages = useCallback(
    ({ inAppMessages }: iterableApi.UserMessages) => {
      const matchingMessages = inAppMessages.filter((message: iterableApi.InAppMessage) => {
        return location.pathname?.includes(message.content.payload.target);
      });
      if (matchingMessages.length > 0) {
        setDisplayedMessages(matchingMessages);
      }
    },
    [location.pathname],
  );

  /**
   * Consumes a given message, and then clear the list of displayed messages.
   *
   * It's possible to avoid clearing the list of displayed messages using `isSilent` param.
   *
   * @param message - InAppMessage to be consumed
   * @param isSilent - determines whether to clear the current list of displayed messages or not
   */
  const consumeMessage = useCallback(
    async (message: iterableApi.InAppMessage, isSilent = false): Promise<void> => {
      const isMessageConsumed = consumedMessages.some(
        (consumedMsg) => consumedMsg.messageId === message.messageId,
      );

      if (!isMessageConsumed) {
        await iterableApi.consumeMessage({ userId, messageId: message.messageId });

        setConsumedMessages([...consumedMessages, message]);

        if (!isSilent) {
          setDisplayedMessages([]);
        }
      }
    },
    [consumedMessages, userId],
  );

  const trackMessageClose = (message: iterableApi.InAppMessage): Promise<void> => {
    return iterableApi.trackMessageClose({ userId, messageId: message.messageId });
  };

  const trackMessageClick = (
    message: iterableApi.InAppMessage,
    clickedUrl: string,
  ): Promise<void> => {
    return iterableApi.trackMessageClick({
      userId,
      messageId: message.messageId,
      clickedUrl,
    });
  };

  const trackMessageDelivery = (message: iterableApi.InAppMessage): Promise<void> => {
    return iterableApi.trackMessageDelivery({ userId, messageId: message.messageId });
  };

  const trackMessageOpen = (message: iterableApi.InAppMessage): Promise<void> => {
    return iterableApi.trackMessageOpen({ userId, messageId: message.messageId });
  };

  useEffect(() => {
    if (!location.pathname?.includes('sites')) return;
    (async () => {
      try {
        const { data } = await iterableApi.getCurrentUserMessages({ userId });
        findMatchingMessages(data);
      } catch (err) {
        setDisplayedMessages([]);
      }
    })();
  }, [userId, location, findMatchingMessages]);

  return {
    displayedMessages,
    consumeMessage,
    trackMessageClose,
    trackMessageClick,
    trackMessageDelivery,
    trackMessageOpen,
  };
};
